import React, { createRef, useEffect, useState } from "react";
import { Button, Segment } from "semantic-ui-react";
import WaveSurfer from "wavesurfer.js";
import TimelinePlugin from "wavesurfer.js/src/plugin/timeline.js";
import "./AudioPlayer.css";

type ButtonPropsType = {
  handleClick: Function;
  disabled: Boolean;
};

const PlayButton = ({ handleClick, disabled }: ButtonPropsType) => (
  <Button
    primary
    content='Play'
    icon='play'
    labelPosition='left'
    onClick={() => handleClick()}
    disabled={disabled ? true : false}
  />
);

const PauseButton = ({ handleClick, disabled }: ButtonPropsType) => (
  <Button
    primary
    content='Pause'
    icon='pause'
    labelPosition='left'
    onClick={() => handleClick()}
    disabled={disabled ? true : false}
  />
);

type AudioPlayerProps = {
  duration: number;
  src: string;
  disablePlay: Boolean;
  disableDownload: Boolean;
};

const AudioPlayer = ({
  duration,
  src,
  disablePlay,
  disableDownload,
}: AudioPlayerProps) => {
  const [playing, setPlaying] = useState<boolean>(false);
  const wavesurferRef = createRef<HTMLDivElement>();
  const timelineRef = createRef<HTMLDivElement>();
  const [wavesurfer, setWavesurfer] = useState<WaveSurfer>();

  useEffect(() => {
    if (wavesurferRef.current) {
   
      const wavesurfer = WaveSurfer.create({
        container: wavesurferRef.current,
        responsive: true,
        waveColor: "#ddd",
        cursorColor: "#cecece",
        barHeight: 400,
        height: 200,
        cursorWidth: 2,
        mediaControls: true,
        plugins: [
          TimelinePlugin.create({
            container: timelineRef.current ? timelineRef.current : undefined,
            timeInterval: (pxPerSec: number) => {
              let timeInterval = 0.5;
              if (duration > 10) {
                timeInterval = 1;
              }
              return timeInterval;
            },
            primaryLabelInterval: (pxPerSec: number) => {
              return 5;
            },
            secondaryLabelInterval: (pxPerSec: number) => {
              return 5;
            },
          }),
        ],
      });

      wavesurfer.load(src);
      wavesurfer.on("finish", () => {
        resetAudio();
        wavesurfer.stop();
      });

      setWavesurfer(wavesurfer);

      return () => {
        console.log("Destroying wavesurfer")
        resetAudio();
        wavesurfer.stop();
        wavesurfer.destroy();
        setWavesurfer(undefined);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = src;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetAudio = () => {
    setPlaying(false);
  };

  const playAudio = () => {
    if (wavesurfer) {
      if (!playing) {
        setPlaying(true);
        wavesurfer.play();
        return;
      }
      setPlaying(false);
      wavesurfer.pause();
    }
  };

  return (
    <Segment color='purple' style={{ height: "306.5px" }}>
      <div style={{ margin: "20px" }}>
        <div id='waveform' ref={wavesurferRef} />
        <div
          id='waveform-timeline'
          ref={timelineRef}
          style={{ maxWidth: "600px" }}
        />
        <div style={{ textAlign: "center" }}>
          {playing ? (
            <PauseButton handleClick={playAudio} disabled={disablePlay} />
          ) : (
            <PlayButton handleClick={playAudio} disabled={disablePlay} />
          )}
          <Button
            primary
            content='Download'
            icon='download'
            labelPosition='left'
            onClick={() => handleDownload()}
            disabled={disableDownload ? true : false}
          />
        </div>
      </div>
    </Segment>
  );
};

export default AudioPlayer;
